import { Calculator, Car, Check, Home, Info, Landmark, Plus, Trash2, Wallet } from "lucide-react";
import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import { MainCard } from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Switch } from "src/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { cn } from "src/lib/utils";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import { simulate, type ExtraMode, type SimulationResult } from "../utils/amortization";
import {
  currentMonthInput,
  maskCurrencyInput,
  parseCurrencyInput,
  parseMonthInput,
} from "../utils/format";
import {
  CORRECTION_PRESETS,
  MODALITIES,
  toMonthlyDecimal,
  type AmortizationSystem,
  type CreditModality,
  type RatePeriod,
} from "../utils/rates";

const CALCULATOR_KEY = "calculadora-amortizacao";

export interface ContributionForm {
  id: string;
  amount: string;
  startMonth: string;
  endMonth: string;
  recurring: boolean;
  intervalMonths: string;
}

export interface InsuranceForm {
  id: string;
  amount: string;
  mode: "all" | "custom";
  startMonth: string;
  endMonth: string;
}

export interface SimulatorFormState {
  amount: string;
  startMonth: string;
  system: AmortizationSystem | "";
  rate: string;
  ratePeriod: RatePeriod;
  term: string;
  correctionId: string;
  correctionRate: string;
  modality: CreditModality | "";
  extraMode: ExtraMode;
  contributions: ContributionForm[];
  insurances: InsuranceForm[];
}

function newContribution(): ContributionForm {
  return {
    id: Math.random().toString(36).slice(2),
    amount: "",
    startMonth: "12",
    endMonth: "",
    recurring: false,
    intervalMonths: "12",
  };
}

function newInsurance(): InsuranceForm {
  return {
    id: Math.random().toString(36).slice(2),
    amount: "",
    mode: "all",
    startMonth: "",
    endMonth: "",
  };
}

function defaultFormState(): SimulatorFormState {
  return {
    amount: "",
    startMonth: currentMonthInput(),
    system: "",
    rate: "",
    ratePeriod: "annual",
    term: "",
    correctionId: "none",
    correctionRate: "0",
    modality: "",
    extraMode: "term",
    contributions: [],
    insurances: [],
  };
}

function num(value: string): number {
  return parseFloat(value.replace(/\./g, "").replace(",", ".")) || 0;
}

const MODALITY_ICONS: Record<CreditModality, typeof Home> = {
  imobiliario: Home,
  veiculos: Car,
  consignado: Landmark,
  pessoal: Wallet,
};

function FieldHint({ text }: { text: string }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground ml-1">
            <Info className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs bg-[#9c1006] text-white">{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function AmortizacaoInputCard({
  setResults,
}: {
  setResults: (value: AmortizacaoResultType | null) => void;
}) {
  const isVerified = useIsVerified();
  const [state, setState] = useState<SimulatorFormState>(defaultFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState("");

  const update = <K extends keyof SimulatorFormState>(key: K, value: SimulatorFormState[K]) =>
    setState((s) => ({ ...s, [key]: value }));

  useEffect(() => {
    const saved = loadSavedCalculatorData(CALCULATOR_KEY) as SimulatorFormState | null;
    if (saved) setState({ ...defaultFormState(), ...saved });
  }, []);

  const applyModality = (id: CreditModality) => {
    const m = MODALITIES.find((x) => x.id === id)!;
    setState((s) => ({
      ...s,
      modality: id,
      system: m.recommendedSystem,
      rate: String(m.typicalAnnualRate).replace(".", ","),
      ratePeriod: "annual",
      term: String(m.typicalTermMonths),
    }));
  };

  const setCorrection = (id: string) => {
    const preset = CORRECTION_PRESETS.find((p) => p.id === id);
    setState((s) => ({
      ...s,
      correctionId: id,
      correctionRate:
        id === "custom" ? s.correctionRate : String(preset?.monthly ?? 0).replace(".", ","),
    }));
  };

  const updateContribution = (id: string, patch: Partial<ContributionForm>) =>
    setState((s) => ({
      ...s,
      contributions: s.contributions.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    }));

  const removeContribution = (id: string) =>
    setState((s) => ({ ...s, contributions: s.contributions.filter((c) => c.id !== id) }));

  const updateInsurance = (id: string, patch: Partial<InsuranceForm>) =>
    setState((s) => ({
      ...s,
      insurances: s.insurances.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    }));

  const removeInsurance = (id: string) =>
    setState((s) => ({ ...s, insurances: s.insurances.filter((i) => i.id !== id) }));

  function clear() {
    setState(defaultFormState());
    setErrors({});
    setMessage("");
    setResults(null);
  }

  function calcular() {
    const e: Record<string, string> = {};
    const principal = parseCurrencyInput(state.amount);
    const rate = num(state.rate);
    const term = parseInt(state.term, 10) || 0;

    if (principal <= 0) e.amount = "Informe o valor do empréstimo.";
    if (!state.system) e.system = "Escolha o sistema.";
    if (rate <= 0) e.rate = "Informe a taxa de juros.";
    if (term <= 0) e.term = "Informe a quantidade de parcelas.";

    setErrors(e);
    if (Object.keys(e).length > 0) return;

    const monthlyRate = toMonthlyDecimal(rate, state.ratePeriod);
    const monthlyCorrection =
      state.correctionId === "none" ? 0 : num(state.correctionRate) / 100;
    const startDate = parseMonthInput(state.startMonth);

    const res: SimulationResult = simulate({
      principal,
      monthlyRate,
      termMonths: term,
      system: state.system as AmortizationSystem,
      monthlyCorrection,
      startDate,
      extraMode: state.extraMode,
      contributions: state.contributions.map((c) => ({
        amount: parseCurrencyInput(c.amount),
        startMonth: parseInt(c.startMonth, 10) || 0,
        endMonth: parseInt(c.endMonth, 10) || 0,
        recurring: c.recurring,
        intervalMonths: parseInt(c.intervalMonths, 10) || 12,
      })),
      insurances: state.insurances.map((ins) => ({
        amount: parseCurrencyInput(ins.amount),
        startMonth: ins.mode === "custom" ? parseInt(ins.startMonth, 10) || 1 : 1,
        endMonth: ins.mode === "custom" ? parseInt(ins.endMonth, 10) || 0 : 0,
      })),
    });

    setResults({
      ...res,
      system: state.system as AmortizationSystem,
      extraMode: state.extraMode,
      startDate,
    });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage("");
    saveCalculatorData(CALCULATOR_KEY, state as unknown as Record<string, unknown>);
    checkCalculatorUsageLimitAndRedirect(calcular);
  }

  const lastInstallment = state.term || "360";

  return (
    <MainCard noPadding noBorder className="bg-transparent rounded-none">
      <div className="rounded-2xl border border-border bg-card p-4 shadow-md overflow-hidden sm:p-8">
        <div className="mb-5 flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Calculator className="h-5 w-5" />
          </span>
          <h2 className="text-xl font-bold text-primary">Parâmetros</h2>
        </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Modality presets */}
        <div>
          <p className="mb-2 text-sm font-medium">
            Modalidade{" "}
            <span className="text-muted-foreground">(opcional, preenche valores típicos)</span>
          </p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {MODALITIES.map((m) => {
              const Icon = MODALITY_ICONS[m.id];
              const active = state.modality === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => applyModality(m.id)}
                  className={cn(
                    "flex flex-col items-start gap-1.5 rounded-xl border p-3 text-left transition-colors",
                    active
                      ? "border-primary bg-white ring-1 ring-primary"
                      : "border-border bg-white hover:border-primary/40 hover:bg-gray-50",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <Icon className={cn("h-5 w-5 shrink-0", active ? "text-primary" : "text-muted-foreground")} />
                    <span className="text-sm font-semibold">{m.label}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{m.description}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main parameters */}
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="block mb-2 font-medium text-sm">
              Valor do empréstimo <span className="text-destructive">*</span>
              <FieldHint text="Valor total que será financiado, antes dos juros." />
            </label>
            <div className={cn("flex border", errors.amount && "border-red-500")}>
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">R$</span>
              <input
                className="flex-1 p-2 min-w-0 outline-none"
                inputMode="numeric"
                placeholder="0,00"
                value={state.amount}
                onChange={(e) => update("amount", maskCurrencyInput(parseCurrencyInput(e.target.value)))}
              />
            </div>
            {errors.amount && <p className="mt-1 text-sm text-destructive">{errors.amount}</p>}
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">Data de início</label>
            <input
              type="month"
              className="w-full max-w-full p-2 border outline-none"
              value={state.startMonth}
              onChange={(e) => update("startMonth", e.target.value)}
            />
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">
              Sistema de amortização <span className="text-destructive">*</span>
              <FieldHint text="SAC tem parcelas decrescentes; Price tem parcelas fixas." />
            </label>
            <Select value={state.system} onValueChange={(v) => update("system", v as AmortizationSystem)}>
              <SelectTrigger className={cn(errors.system && "border-destructive")}>
                <SelectValue placeholder="Selecione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PRICE">Tabela Price — parcelas fixas</SelectItem>
                <SelectItem value="SAC">Tabela SAC — parcelas decrescentes</SelectItem>
              </SelectContent>
            </Select>
            {errors.system && <p className="mt-1 text-sm text-destructive">{errors.system}</p>}
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">
              Taxa de juros (%) <span className="text-destructive">*</span>
              <FieldHint text="Taxa de juros efetiva. Escolha ao lado se é mensal (a.m.) ou anual (a.a.)." />
            </label>
            <input
              className={cn("w-full p-2 border outline-none", errors.rate && "border-red-500")}
              placeholder="0,00"
              value={state.rate}
              onChange={(e) => update("rate", e.target.value.replace(/[^\d,.]/g, ""))}
            />
            {errors.rate && <p className="mt-1 text-sm text-destructive">{errors.rate}</p>}
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">Período dos juros</label>
            <Select value={state.ratePeriod} onValueChange={(v) => update("ratePeriod", v as RatePeriod)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensal (a.m.)</SelectItem>
                <SelectItem value="annual">Anual (a.a.)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">
              Quantidade de parcelas <span className="text-destructive">*</span>
            </label>
            <input
              className={cn("w-full p-2 border outline-none", errors.term && "border-red-500")}
              inputMode="numeric"
              placeholder="360"
              value={state.term}
              onChange={(e) => update("term", e.target.value.replace(/\D/g, ""))}
            />
            {errors.term && <p className="mt-1 text-sm text-destructive">{errors.term}</p>}
          </div>

          <div>
            <label className="block mb-2 font-medium text-sm">
              Correção monetária
              <FieldHint text="Índice mensal aplicado ao saldo devedor (ex.: TR, IPCA). Opcional." />
            </label>
            <Select value={state.correctionId} onValueChange={setCorrection}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CORRECTION_PRESETS.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
                <SelectItem value="custom">Personalizada…</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {state.correctionId === "custom" && (
            <div>
              <label className="block mb-2 font-medium text-sm">Correção mensal (%)</label>
              <input
                className="w-full p-2 border outline-none"
                placeholder="0,00"
                value={state.correctionRate}
                onChange={(e) => update("correctionRate", e.target.value.replace(/[^\d,.]/g, ""))}
              />
            </div>
          )}
        </div>

        {/* Extra amortization */}
        <div id="amortizacao-extra" className="rounded-xl border border-border bg-secondary/40 p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold">Amortização extra</p>
              <p className="text-sm text-muted-foreground">
                Aportes para reduzir o prazo ou o valor das parcelas.
              </p>
            </div>
            <div className="flex rounded-lg border border-border bg-card p-0.5 text-sm">
              <button
                type="button"
                onClick={() => update("extraMode", "term")}
                className={cn(
                  "rounded-md px-3 py-1.5 font-medium transition-colors",
                  state.extraMode === "term"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Reduzir prazo
              </button>
              <button
                type="button"
                onClick={() => update("extraMode", "installment")}
                className={cn(
                  "rounded-md px-3 py-1.5 font-medium transition-colors",
                  state.extraMode === "installment"
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                Reduzir parcela
              </button>
            </div>
          </div>

          {state.contributions.map((c) => (
            <div
              key={c.id}
              className="mt-5 grid gap-5 rounded-lg border border-border bg-card p-2 sm:grid-cols-[1fr_auto]"
            >
              <div className="grid gap-4 min-w-0 grid-cols-4 max-sm:grid-cols-1">
                <div className="min-w-0">
                  <label className="block mb-1 text-sm font-medium">Valor do aporte</label>
                  <div className="flex min-w-0 border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">R$</span>
                    <input
                      className="flex-1 p-2 min-w-0 outline-none"
                      inputMode="numeric"
                      placeholder="0,00"
                      value={c.amount}
                      onChange={(e) =>
                        updateContribution(c.id, {
                          amount: maskCurrencyInput(parseCurrencyInput(e.target.value)),
                        })
                      }
                    />
                  </div>
                </div>
                <div className="min-w-0">
                  <label className="block mb-1 text-sm font-medium">Na parcela nº</label>
                  <input
                    className="w-full p-2 border outline-none"
                    inputMode="numeric"
                    placeholder="12"
                    value={c.startMonth}
                    onChange={(e) =>
                      updateContribution(c.id, { startMonth: e.target.value.replace(/\D/g, "") })
                    }
                  />
                </div>
                {c.recurring && (
                  <div className="min-w-0">
                    <label className="block mb-1 text-sm font-medium">Até a parcela nº</label>
                    <input
                      className="w-full p-2 border outline-none"
                      inputMode="numeric"
                      placeholder={lastInstallment}
                      value={c.endMonth}
                      onChange={(e) =>
                        updateContribution(c.id, { endMonth: e.target.value.replace(/\D/g, "") })
                      }
                    />
                  </div>
                )}
                <div className="min-w-0">
                  <label className="block mb-1 text-sm font-medium">
                    {c.recurring ? "A cada (meses)" : "Tipo de aporte"}
                  </label>
                  {c.recurring ? (
                    <input
                      className="w-full p-2 border outline-none"
                      inputMode="numeric"
                      placeholder="12"
                      value={c.intervalMonths}
                      onChange={(e) =>
                        updateContribution(c.id, {
                          intervalMonths: e.target.value.replace(/\D/g, ""),
                        })
                      }
                    />
                  ) : (
                    <div className="flex h-11 items-center text-sm text-muted-foreground">
                      Único
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-center justify-between gap-3 max-sm:flex-col max-sm:items-start max-sm:justify-start">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={c.recurring}
                    onCheckedChange={(v) => updateContribution(c.id, { recurring: v })}
                  />
                  Recorrente
                </label>
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    className="bg-gradient-to-br from-[#9c1006] to-[#6e0b04] shadow text-xs h-8 gap-1 rounded-full px-3 text-white hover:opacity-90"
                  >
                    <Check className="h-3.5 w-3.5" /> Salvar
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeContribution(c.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() =>
              setState((s) => ({ ...s, contributions: [...s.contributions, newContribution()] }))
            }
          >
            <Plus className="mr-1.5 h-4 w-4" /> Adicionar aporte
          </Button>
        </div>

        {/* Insurance / fees */}
        <div className="rounded-xl border border-border bg-secondary/40 p-6">
          <div>
            <p className="text-sm font-semibold">Seguros / Taxas</p>
            <p className="text-sm text-muted-foreground">
              Valor mensal somado à parcela (seguro, taxa administrativa, etc.).
            </p>
          </div>

          {state.insurances.map((ins) => (
            <div
              key={ins.id}
              className="mt-5 grid gap-5 rounded-lg border border-border bg-card p-3 sm:grid-cols-[1fr_auto]"
            >
              <div className="grid gap-5 min-w-0 overflow-hidden sm:grid-cols-3">
                <div className="min-w-0">
                  <label className="block mb-1 text-sm font-medium">Valor de taxas/seguros</label>
                  <div className="flex min-w-0 border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">R$</span>
                    <input
                      className="flex-1 p-2 min-w-0 outline-none"
                      inputMode="numeric"
                      placeholder="0,00"
                      value={ins.amount}
                      onChange={(e) =>
                        updateInsurance(ins.id, {
                          amount: maskCurrencyInput(parseCurrencyInput(e.target.value)),
                        })
                      }
                    />
                  </div>
                </div>
                <div className={cn("min-w-0", ins.mode === "custom" ? "sm:col-span-1" : "sm:col-span-2")}>
                  <label className="block mb-1 text-sm font-medium">Inserir taxas em</label>
                  <div className="flex overflow-hidden rounded-lg border border-border bg-card p-0.5 text-sm">
                    <button
                      type="button"
                      onClick={() => updateInsurance(ins.id, { mode: "all" })}
                      className={cn(
                        "flex-1 min-w-0 rounded-md px-2 py-1.5 font-medium transition-colors text-center",
                        ins.mode === "all"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      Todas as parcelas
                    </button>
                    <button
                      type="button"
                      onClick={() => updateInsurance(ins.id, { mode: "custom" })}
                      className={cn(
                        "flex-1 min-w-0 rounded-md px-2 py-1.5 font-medium transition-colors text-center",
                        ins.mode === "custom"
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      Intervalo customizado
                    </button>
                  </div>
                </div>
                {ins.mode === "custom" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block mb-1 text-sm font-medium">Da parcela</label>
                      <input
                        className="w-full p-2 border outline-none"
                        inputMode="numeric"
                        placeholder="1"
                        value={ins.startMonth}
                        onChange={(e) =>
                          updateInsurance(ins.id, { startMonth: e.target.value.replace(/\D/g, "") })
                        }
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium">Até a parcela</label>
                      <input
                        className="w-full p-2 border outline-none"
                        inputMode="numeric"
                        placeholder={lastInstallment}
                        value={ins.endMonth}
                        onChange={(e) =>
                          updateInsurance(ins.id, { endMonth: e.target.value.replace(/\D/g, "") })
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end">
                <div className="flex items-center gap-2">
                  <Button
                    type="submit"
                    size="sm"
                    className="bg-gradient-to-br from-[#9c1006] to-[#6e0b04] shadow text-xs h-8 gap-1 rounded-full px-3 text-white hover:opacity-90"
                  >
                    <Check className="h-3.5 w-3.5" /> Salvar
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={() => removeInsurance(ins.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() =>
              setState((s) => ({ ...s, insurances: [...s.insurances, newInsurance()] }))
            }
          >
            <Plus className="mr-1.5 h-4 w-4" /> Adicionar seguro/taxa
          </Button>
        </div>

        {message && <p className="text-center text-sm font-bold text-destructive">{message}</p>}

        <div className="flex items-center gap-2 max-sm:flex-col max-sm:w-full">
          <Button
            type="submit"
            disabled={!isVerified}
            className="h-10 flex-1 rounded-md px-4 text-sm sm:px-8 sm:text-base bg-gradient-to-br from-[#9c1006] to-[#6e0b04] text-white shadow hover:opacity-90 max-sm:w-full"
          >
            Simular amortização
          </Button>
          <Button type="button" variant="outline" className="shrink-0 max-sm:w-full" onClick={clear}>
            Limpar
          </Button>
        </div>
      </form>
      </div>
    </MainCard>
  );
}
